import { useEffect, useReducer } from 'react';
import { Alert, Badge, Box, Button, Flex, Heading, SimpleGrid, Spinner, Stack, Text } from '@chakra-ui/react';
import { Link as RouterLink, useParams } from 'react-router-dom';
import { getPublicStockCustomMetrics } from '../../api';
import { useColorModeValue } from '../../components/ui/color-mode';
import NotFound from '../NotFound';
import {
    formatMetricDate,
    formatMetricValue,
    publicMetricCtaPath,
    PublicPageShell,
} from './PublicMetricsShared';
import { PublicMetricLink } from './PublicMetricLink';

const stockMetricsInitialState = { status: 'loading', payload: null, error: null };

function stockMetricsReducer(state, action) {
    switch (action.type) {
        case 'loading':
            return stockMetricsInitialState;
        case 'success':
            return { status: 'success', payload: action.payload, error: null };
        case 'error':
            return {
                status: action.status,
                payload: null,
                error: action.error,
            };
        default:
            return state;
    }
}

function MetricCard({ metric }) {
    const borderColor = useColorModeValue('gray.200', 'gray.700');
    const muted = useColorModeValue('gray.600', 'gray.300');
    const latest = metric.latest_value || {};

    return (
        <Box borderWidth="1px" borderColor={borderColor} borderRadius="lg" p={5}>
            <Flex justify="space-between" align="flex-start" gap={3} mb={3}>
                <Box>
                    <Heading size="md">
                        <PublicMetricLink metric={metric} />
                    </Heading>
                    <Text mt={1} color={muted} fontSize="sm">
                        {metric.value_count} historical values
                    </Text>
                </Box>
                <Badge colorPalette="green" variant="subtle">
                    Public data
                </Badge>
            </Flex>
            <Text fontSize="2xl" fontWeight="bold">
                {formatMetricValue(latest.value, latest.unit || metric.unit)}
            </Text>
            <Text color={muted} fontSize="sm">
                Latest period: {formatMetricDate(latest.period_end)}
            </Text>
            {metric.description && (
                <Text mt={3} color={muted} fontSize="sm" lineClamp={3}>
                    {metric.description}
                </Text>
            )}
        </Box>
    );
}

function PublicStockMetricsPage() {
    const { ticker } = useParams();
    const normalizedTicker = String(ticker || '').toUpperCase();
    const [state, dispatch] = useReducer(stockMetricsReducer, stockMetricsInitialState);

    useEffect(() => {
        let cancelled = false;
        dispatch({ type: 'loading' });

        getPublicStockCustomMetrics(normalizedTicker)
            .then((payload) => {
                if (!cancelled) dispatch({ type: 'success', payload });
            })
            .catch((error) => {
                if (!cancelled) {
                    dispatch({
                        type: 'error',
                        status: error.status === 404 ? 'not-found' : 'error',
                        error: error.message,
                    });
                }
            });

        return () => {
            cancelled = true;
        };
    }, [normalizedTicker]);

    const metrics = state.payload?.metrics || [];

    if (state.status === 'not-found') {
        return <NotFound />;
    }

    return (
        <PublicPageShell>
            <Stack gap={6}>
                <Box>
                    <Text color="gray.500" fontWeight="semibold" textTransform="uppercase" fontSize="sm">
                        Company KPI data
                    </Text>
                    <Heading as="h1" size="2xl" mt={2}>
                        {normalizedTicker} Public Metrics
                    </Heading>
                    <Text mt={3} color="gray.600" maxW="760px">
                        Filing-sourced operating metrics approved for public historical data pages.
                    </Text>
                </Box>

                {state.status === 'loading' && (
                    <Flex align="center" gap={3}>
                        <Spinner size="sm" />
                        <Text>Loading public metrics…</Text>
                    </Flex>
                )}

                {state.status === 'error' && (
                    <Alert.Root status="error">
                        <Alert.Indicator />
                        <Alert.Content>{state.error || 'Unable to load public metrics.'}</Alert.Content>
                    </Alert.Root>
                )}

                {state.status === 'success' && metrics.length === 0 && (
                    <Box borderWidth="1px" borderColor="gray.200" borderRadius="lg" p={6}>
                        <Heading size="md">No public metrics yet</Heading>
                        <Text mt={2} color="gray.600">
                            No approved, source-backed KPI history is currently published for {normalizedTicker}.
                        </Text>
                    </Box>
                )}

                {metrics.length > 0 && (
                    <>
                        <SimpleGrid columns={{ base: 1, md: 2 }} gap={5}>
                            {metrics.map((metric) => (
                                <MetricCard key={metric.metric_key} metric={metric} />
                            ))}
                        </SimpleGrid>
                        <Flex gap={3} wrap="wrap">
                            <Button
                                as={RouterLink}
                                to={publicMetricCtaPath(normalizedTicker, 'charts', 'stock_index_charts_cta')}
                                colorPalette="blue"
                            >
                                Open charts
                            </Button>
                            <Button
                                as={RouterLink}
                                to={publicMetricCtaPath(normalizedTicker, 'reverse-dcf', 'stock_index_reverse_dcf_cta')}
                                variant="outline"
                            >
                                Start valuation
                            </Button>
                        </Flex>
                    </>
                )}
            </Stack>
        </PublicPageShell>
    );
}

export default PublicStockMetricsPage;
